<template>
  <Toast position="bottom-right" />
  <Toast position="top-right" group="top-right" />
  <Toast
    position="bottom-left"
    group="app-update"
  >
    <template #container="{ message }">
      <div class="flex flex-col gap-4 p-4 shadow-lg">
        <div class="flex w-full items-center justify-between">
          <div class="flex size-10 items-center justify-center rounded-full">
            <Icon name="download" class="size-4 text-[--v3-global-textcolor]" />
          </div>
          <div class="flex-1">
            <p class="v3-body-regular-400-line-height-150 mt-1 text-sm text-[--v3-global-textcolor]">
              {{ message.detail }}
            </p>
          </div>
        </div>
        <Button
          label="Reload"
          size="small"
          @click="handleReload"
        />
      </div>
    </template>
  </Toast>
  <IconProvider />
  <FlagIconProvider />
  <ConfirmDialog group="headless">
    <template
      #container="{
        message,
        acceptCallback,
        rejectCallback,
      }: {
        message: CustomConfirmationOptions
        acceptCallback: () => void
        rejectCallback: () => void
      }"
    >
      <ConfirmFrame
        :header="message.header"
        :message="message.message"
        :accept-label="message.acceptLabel"
        :reject-label="message.rejectLabel"
        :reject-class="message.rejectClass"
        :accept-class="message.acceptClass"
        :has-accept-icon="message.hasAcceptIcon"
        :closeable="message.closeable"
        @on-accept="acceptCallback"
        @on-reject="rejectCallback"
      />
    </template>
  </ConfirmDialog>
  <ConfirmDialog group="connect_shop">
    <template #container="{ acceptCallback, rejectCallback }: { message: ConfirmationOptions, acceptCallback: () => void, rejectCallback: () => void }">
      <BaseDialogFrame
        closeable
        header="Connect Your Etsy Shop"
        header-class="v3-regular-medium-line-height-150 text-[--text-primary]"
        @close="rejectCallback"
      >
        <template #body>
          <p class="v3-body-regular-400-line-height-auto">
            <span class="font-bold">If you manage multiple Etsy shops, please make sure you are signed in to the correct Etsy account before continuing.</span>
            <br><br>
            Connect your Etsy shop to eRank and gain access to additional tools and reports to improve your store.
            <br><br>
            We protect your private data. It is never shared with anyone else. It is never sold to anyone. You can learn more by reading our
            <a href="https://public.erank.com/legal/privacy-policy" class="privacy-link text-[--v3-button-linkbutton-forestgreen]">privacy policy</a>.
            <br><br>
            You can revoke our access to your private data at any time <a href="https://www.etsy.com/your/shops/me/integrations/installed" class="here text-[--v3-button-linkbutton-forestgreen]">here</a>.            
          </p>
        </template>

        <template #footer>
          <Button label="Continue" @click="acceptCallback" />
        </template>
      </BaseDialogFrame>
    </template>
  </ConfirmDialog>
  <ConfirmDialog group="failed_connect_shop">
    <template #container="{ acceptCallback, rejectCallback }: { message: ConfirmationOptions, acceptCallback: () => void, rejectCallback: () => void }">
      <BaseDialogFrame
        closeable
        header="Didn't finish connecting your shop?"
        header-class="v3-regular-medium-line-height-150 text-[--text-primary]"
        @close="rejectCallback"
      >
        <template #body>
          <p class="text-small-normal">
            Totally fine, but just so you know, connecting is <span class="text-small-semi-bold">safe, private,</span> and <span class="text-small-semi-bold">unlocks the tools that help you grow:</span>
          </p>
          <ul class="my-[8px] grid list-disc gap-[8px] pl-5">
            <li class="text-small-normal">
              We protect your private data.
            </li>
            <li class="text-small-normal">
              It is never shared with anyone else.
            </li>
            <li class="text-small-normal">
              It is never sold to anyone.
            </li>
          </ul>
          <p class="text-small-normal">
            Finish connecting to get full SEO insights, listing tracking, and more.
          </p>
        </template>

        <template #footer>
          <Button label="Connect My Shop" class="mt-[8px]" @click="acceptCallback" />
        </template>
      </BaseDialogFrame>
    </template>
  </ConfirmDialog>
  <ConfirmDialog group="reached_quota_connected_shop">
    <template #container="{ acceptCallback, rejectCallback }: { message: ConfirmationOptions, acceptCallback: () => void, rejectCallback: () => void }">
      <BaseDialogFrame
        closeable
        header="You’ve Reached Your Shop Limit"
        header-class="v3-regular-medium-line-height-150 text-[--text-primary]"
        @close="rejectCallback"
      >
        <template #body>
          <p class="text-small-normal">
            You’ve connected the maximum number of shops for your plan.
          </p>
          <p class="text-small-normal">
            To add more, upgrade your account.
          </p>
        </template>

        <template #footer>
          <Button label="View Plans" class="mt-[8px]" @click="acceptCallback" />
        </template>
      </BaseDialogFrame>
    </template>
  </ConfirmDialog>
  <ConfirmDialog
    group="warning"
  >
    <template #container="{ message, rejectCallback, acceptCallback }: { message: ConfirmationOptions, rejectCallback: () => void, acceptCallback: () => void }">
      <BaseDialogFrame :header="message.header" has-divider>
        <template #body>
          <p class="v3-body-regular-400-line-height-1.5 mb-[15px] !font-bold text-[--v3-red-red-600]">
            Warning!
          </p>
          <!-- eslint-disable-next-line vue/no-v-html -->
          <p class="v3-body-regular-400-line-height-1.5 mb-4" v-html="message.message" />
        </template>
        <template #footer>
          <Button
            label="No"
            class="w-full"
            severity="secondary"
            outlined
            @click="rejectCallback"
          />
          <Button
            label="Yes, I confirm"
            class="radius-[6px] w-full gap-x-0 px-[17.5px] py-[10px]"
            severity="danger"
            icon="check"
            icon-class="size-3.5"
            icon-align="right"
            @click="acceptCallback"
          />
        </template>
      </BaseDialogFrame>
    </template>
  </ConfirmDialog>
  <ConfirmDialog
    group="stop_tracking"
    :pt="{
      root: 'w-[450px] max-w-[90vw]'
    }"
  >
    <template #container="{ message, acceptCallback, rejectCallback }: { message: ConfirmationOptions, acceptCallback: () => void, rejectCallback: () => void }">
      <div class="flex flex-col gap-6 rounded-md bg-white p-6 pb-0">
        <h5 class="v3-h5-semibold-600-line-height-auto text-[--v3-global-textsecondarycolor]">
          {{ message.header }}
        </h5>
        <p class="v3-body-regular-400-line-height-auto text-[--v3-global-textsecondarycolor]">
          {{ message.message }}
        </p>
        <div class="flex items-center gap-3 pb-6 pt-3">
          <Button
            class="flex w-full items-center justify-center gap-2"
            label="Yes"
            @click="acceptCallback"
          >
            <span class="v3-h6-bold-700-line-height-1.2">Yes</span>
            <Icon name="check" :width="14" :height="14" />
          </Button>
          <Button
            class="w-full"
            outlined
            severity="secondary"
            label="No"
            @click="rejectCallback"
          />
        </div>
      </div>
    </template>
  </ConfirmDialog>
  <ConfirmDialog
    group="confirmation_upgrade"
    :pt="{
      root: 'w-[450px] max-w-[90vw]'
    }"
  >
    <template #container="{ acceptCallback, rejectCallback }: { message: ConfirmationOptions, acceptCallback: () => void, rejectCallback: () => void }">
      <div class="flex flex-col gap-6 rounded-md bg-white p-6 pb-0">
        <h5 class="v3-h5-semibold-600-line-height-auto text-[--v3-global-textsecondarycolor]">
          Would you like to go ahead and upgrade your plan?
        </h5>
        <p class="v3-body-regular-400-line-height-auto text-[--v3-global-textsecondarycolor]">
          Upgrading will apply the changes immediately.
        </p>
        <div class="flex items-center gap-3 pb-6 pt-3">
          <Button
            class="w-full"
            outlined
            severity="secondary"
            label="Cancel"
            @click="rejectCallback"
          />
          <Button
            class="flex w-full items-center justify-center gap-2"
            label="Proceed"
            @click="acceptCallback"
          />
        </div>
      </div>
    </template>
  </ConfirmDialog>
  <DynamicDialog />
  <template v-if="isRoutedLoaded">
    <template v-if="authStore.isUserLogged && !isOnboarding">
      <Sidebar class="fixed z-[100]" />
      <template v-if="authStore.isUserLogged">
        <div
          class="flex min-h-screen flex-col bg-[--v3-surface-surface-100]"
        >
          <main
            class="isolate transition-all duration-150"
            :class="{
              'ml-0 md:ml-[277px]': !collapsed ,
              'ml-0 md:ml-[50px]': collapsed,
              'bg-[--color-neutral-white]': route.path === '/create-video' || route.path.startsWith('/traffic-stats') || route.path.startsWith('/shop-info'),
              'bg-[--v3-surface-surface-100]': route.path !== '/create-video' || !route.path.startsWith('/traffic-stats'),
            }"
          >
            <Topbar />
            <section class="relative py-[32px]" :class="customPadding">
              <RouterView />
              <Teleport to="body">
                <KeywordListMenu v-if="hasKeywordMenu" />
              </Teleport>
              <div
                style="margin-top: auto;"
                class="footer-container w-full bg-gray-100 transition-all duration-150"
              >
                <div class="mt-auto flex justify-center">
                  <Footer class="w-full max-w-[1442px] transition-all duration-150 " />
                </div>
              </div>
            </section>
          </main>
        </div>
      </template>
      <template v-else>
        <Topbar />
        <section class="pt-[32px]">
          <RouterView />
        </section>
        <div class="flex justify-center">
          <Footer class="w-full max-w-[1442px] transition-all duration-150" />
        </div>
      </template>
    </template>
    <template v-else-if="isOnboarding">
      <section class="min-h-screen bg-[#FEFBF5]">
        <RouterView />
      </section>
    </template>
    <template v-else>
      <main :class="cx('bg-white transition-all duration-150 ease-in-out', backgroundColor)">
        <Sidebar class="fixed z-[100]" />
        <Topbar :class="cx('transition-all duration-150 ease-in-out', backgroundColor)" />
        <section class="min-h-screen px-[10px] pt-[32px] md:px-[50px]">
          <RouterView />
        </section>
        <div class="flex justify-center">
          <Footer :class="cx('w-full max-w-[1442px] transition-all duration-150 ease-in-out', backgroundColor)" />
        </div>
      </main>
    </template>
    <ConnectEtsyShopSuccess :redirect-to="pageSlug" />
    <ConnectEtsyShopExist :redirect-to="redirectTo" />
  </template>
  <VueQueryDevtools position="right" />
  <JumpToTopButton />
</template>

<script lang="ts" setup>
import { VueQueryDevtools } from '@tanstack/vue-query-devtools';
import { useUrlSearchParams } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import type { ConfirmationOptions } from 'primevue/confirmationoptions';
type CustomConfirmationOptions = ConfirmationOptions & { closeable?: boolean, hasAcceptIcon?: boolean };
import ConfirmDialog from 'primevue/confirmdialog';
import DynamicDialog from 'primevue/dynamicdialog';
import Toast from 'primevue/toast';
import { computed,onMounted,onUnmounted, ref, watch } from 'vue';
import { useRoute,useRouter } from 'vue-router';

import Button from '@/common/components/atoms/Button';
import FlagIconProvider from '@/common/components/atoms/FlagIconProvider';
import Icon from '@/common/components/atoms/Icon';
import IconProvider from '@/common/components/atoms/IconProvider';
import JumpToTopButton from '@/common/components/atoms/JumpToTopButton';
import BaseDialogFrame from '@/common/components/molecules/BaseDialogFrame';
import ConnectEtsyShopExist from '@/common/components/molecules/ConnectEtsyShopExist';
import ConnectEtsyShopSuccess from '@/common/components/molecules/ConnectEtsyShopSuccess';
import ConfirmFrame from '@/common/components/organisms/ConfirmFrame';
import KeywordListMenu from '@/common/components/organisms/KeywordListMenu';
import { mixPanelEvents } from '@/common/constants';
import { useAuthStore } from '@/common/stores/auth';
import { useIntercomStore } from '@/common/stores/intercom';
import { useKeywordListStore } from '@/common/stores/lists';
import { useTrackedToast } from '@/composables/useTrackedToast';
import { track } from '@/helpers/analytics/mixpanel';
import { startVersionCheck } from '@/helpers/app-version';
import Footer from '@/modules/layout/components/organisms/Footer';
import Sidebar from '@/modules/layout/components/organisms/Sidebar';
import Topbar from '@/modules/layout/components/organisms/Topbar';
import { useNotificationStore } from '@/modules/layout/stores/notification';
import { useSidebarStore } from '@/modules/layout/stores/sidebar';
import { usePaddleStore } from '@/modules/pricing/stores/paddle';

import { KEYWORD_MENU_ROUTES } from './constants';
import { provideBackgroundColor } from './helpers/background-context';
import { cx } from './helpers/cva';

const isRoutedLoaded = ref(false);
const windowLastActiveListId = window.__DATA__?.user?.current_keyword_list?.id;

const authStore = useAuthStore();
const paddleStore = usePaddleStore();
const router = useRouter();
const route = useRoute();
const { collapsed } = storeToRefs(useSidebarStore());
const intercomStore = useIntercomStore();
const { notification } = storeToRefs(useNotificationStore());
const toast = useTrackedToast();
const params = useUrlSearchParams('history');
const onboardingRoutes = ['registration', 'login', 'auth.socialite.redirect.entry', 'reset-password', 'survey', 'expired', 'magic', 'settings.confirm-email'];
const currentVersion = import.meta.env.VITE_APP_VERSION as string | undefined;
const versionUrl = '/build/version.json';
let stopVersionCheck: (() => void) | null = null;

const backgroundColor = ref<string>('');
provideBackgroundColor(backgroundColor);

const redirectTo = computed(() => route.matched[0]?.path.substring(1) ?? '');
const pageSlug = computed(() => route?.meta?.metaTags?.find((tag) => tag.page_slug)?.page_slug ?? '');
const hasKeywordMenu = computed(() => {
  const parentRoute = route.matched[0].name.toString();

  return KEYWORD_MENU_ROUTES.includes(parentRoute);
});

const isOnboarding = computed(() => {
  const currentRoute = route.matched[0].name.toString();

  return onboardingRoutes.includes(currentRoute);
});

const handleReload = () => {
  track('Clicked Reload', {
    'Erank Version': 3,
  });
  window.location.reload();
};

const shouldShowIntercom = computed(() => {
  // Hide Intercom on /survey route
  const currentRoute = route.matched[0]?.name?.toString() || '';
  return authStore.isUserLogged && !isOnboarding.value && currentRoute !== 'survey.entry';
});

onMounted(async () => {
  await router.isReady();
  isRoutedLoaded.value = true;

  if (windowLastActiveListId) {
    useKeywordListStore().lastActiveListId = windowLastActiveListId;
  }

  paddleStore.loadPaddle().then(() => {
    if (!authStore.isUserLogged) return;
    const { email } = authStore.user;
    if (paddleStore.paddle) {
      paddleStore.updatePaddleUsers(email);
    }
  }).catch((error) => {
    console.error('Error loading Paddle:', error);
  });
  
  if (shouldShowIntercom.value) {
    intercomStore.fetchIntercom();
  }

  if(params.success == 'false' && params.access_grant == 'refused') {
    // Track the failed/refused Etsy connection
    track(mixPanelEvents.CLICK_CONNECT_SHOP_CANCEL_BUTTON, {
      Module: pageSlug.value,
      'Source': mixPanelEvents.REFUSED_ETSY_AUTHORIZATION,
      'Erank Version': 3,
    });
  }

  stopVersionCheck = startVersionCheck({
    currentVersion,
    versionUrl,
    onUpdate: () => {
      toast.add({
        severity: 'info',
        detail: 'A new version is available. Refresh to update.',
        life: 0,
        closable: true,
        group: 'app-update',
      });
    },
  });
});

onUnmounted(() => {
  stopVersionCheck?.();
});

watch(
  () => notification.value,
  (value) => {
    if (value) {
      toast.add({
        severity: value.type,
        summary: value.title,
        detail: value.message,
        life: 5000,
      });
    }
  },
);
const customPadding = computed(() => {
  const unpaddedRoutes = ['/rank-checker', '/sales-map'];
  return unpaddedRoutes.includes(route.path) ? '' : 'px-4 md:px-[50px]';
});
</script>
